import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #tryPlay = () => {
    const { videoElement } = this.refs;
    if (!videoElement) return;

    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.loop = true;
    videoElement.autoplay = true;
    videoElement.playsInline = true;
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('loop', '');
    videoElement.setAttribute('autoplay', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');
    videoElement.play?.().catch(() => {});
  };

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.loop = true;
    videoElement.autoplay = true;
    videoElement.playsInline = true;
    videoElement.removeAttribute('controls');
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('loop', '');
    videoElement.setAttribute('autoplay', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');
    videoElement.setAttribute('x-webkit-airplay', 'deny');

    videoElement.load();
    this.#tryPlay();

    videoElement.addEventListener('loadedmetadata', this.#tryPlay);
    videoElement.addEventListener('canplay', this.#tryPlay);
    document.addEventListener('visibilitychange', this.#tryPlay);
    window.addEventListener('pageshow', this.#tryPlay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { videoElement } = this.refs;
    videoElement?.removeEventListener('loadedmetadata', this.#tryPlay);
    videoElement?.removeEventListener('canplay', this.#tryPlay);
    document.removeEventListener('visibilitychange', this.#tryPlay);
    window.removeEventListener('pageshow', this.#tryPlay);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}